import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input, Button, Spin, Tooltip } from "antd";
import {
  SearchOutlined,
  AimOutlined,
  GlobalOutlined,
  LockOutlined,
  UnlockOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import styles from "./LocationPicker.module.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const NOMINATIM_SEARCH =
  "https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5";

function normalizePosition(value) {
  if (!value || typeof value !== "object") return null;
  const lat = Number(value.lat);
  const lon = Number(value.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return [lat, lon];
}

function samePosition(a, b) {
  if (!a || !b) return false;
  return Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);
}

function themeColor() {
  if (typeof document === "undefined") return "#5e4ce2";
  return getComputedStyle(document.documentElement)
    .getPropertyValue("--zh-brand-primary").trim() || "#5e4ce2";
}

function pinSvg(fill) {
  const white = "#fff";
  return `<div style="width:32px;height:44px;position:relative;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.25))">
    <svg viewBox="0 0 32 44" fill="none" style="display:block">
      <path d="M16 2C9.4 2 4 7.4 4 14c0 8.5 12 27.5 12 27.5S28 22.5 28 14c0-6.6-5.4-12-12-12z" fill="${fill}"/>
      <circle cx="16" cy="14" r="7" fill="${white}"/>
      <circle cx="16" cy="14" r="4.5" fill="${fill}"/>
    </svg>
  </div>`;
}

function LockedIcon() {
  return L.divIcon({
    className: "",
    html: pinSvg(themeColor(), true),
    iconSize: [32, 44],
    iconAnchor: [16, 44],
  });
}

function MapEvents({ onMapClick, editing }) {
  useMapEvents({
    click(e) {
      if (editing) onMapClick(e.latlng);
    },
  });
  return null;
}

function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom() < 10 ? 14 : map.getZoom());
  }, [center, map]);
  return null;
}

function LocationPicker({ value, onChange, address }) {
  const markerRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [initialPosition] = useState(() => normalizePosition(value));

  const [locked, setLocked] = useState(() => !!initialPosition);
  const [confirmedPosition, setConfirmedPosition] = useState(initialPosition);
  const [draftPosition, setDraftPosition] = useState(initialPosition);
  const [center, setCenter] = useState(
    initialPosition || DEFAULT_CENTER
  );

  const debounceRef = useRef(null);

  useEffect(() => {
    const pos = normalizePosition(value);
    if (!pos || samePosition(pos, confirmedPosition)) return;
    setConfirmedPosition(pos);
    setDraftPosition(pos);
    setCenter(pos);
    setLocked(true);
  }, [value, confirmedPosition, locked]);

  const enterEditMode = useCallback(() => {
    setDraftPosition(confirmedPosition);
    if (confirmedPosition) setCenter(confirmedPosition);
    setLocked(false);
  }, [confirmedPosition]);

  const handleConfirm = useCallback(() => {
    if (!draftPosition) return;
    setConfirmedPosition(draftPosition);
    setLocked(true);
    const [lat, lon] = draftPosition;
    onChange?.({ lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) });
  }, [draftPosition, onChange]);

  const handleCancel = useCallback(() => {
    setDraftPosition(confirmedPosition);
    setSearchText("");
    setSearchResults([]);
    setShowResults(false);
    if (confirmedPosition) setCenter(confirmedPosition);
    setLocked(true);
  }, [confirmedPosition]);

  const handleMapClick = useCallback(
    (latlng) => {
      const lat = Number(latlng.lat.toFixed(6));
      const lon = Number(latlng.lng.toFixed(6));
      setDraftPosition([lat, lon]);
      setCenter([lat, lon]);
    },
    []
  );

  const handleMarkerDrag = useCallback(
    (e) => {
      const latlng = e.target.getLatLng();
      const lat = Number(latlng.lat.toFixed(6));
      const lon = Number(latlng.lng.toFixed(6));
      setDraftPosition([lat, lon]);
      setCenter([lat, lon]);
    },
    []
  );

  const handleSearch = useCallback((e) => {
    const text = e.target.value;
    setSearchText(text);
    if (!text.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `${NOMINATIM_SEARCH}&q=${encodeURIComponent(text)}`,
          { headers: { Accept: "application/json" } }
        );
        if (!res.ok) throw new Error("Search failed");
        const data = await res.json();
        setSearchResults(Array.isArray(data) ? data : []);
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, []);

  const handleSelectResult = useCallback((result) => {
    const lat = Number(parseFloat(result.lat).toFixed(6));
    const lon = Number(parseFloat(result.lon).toFixed(6));
    setDraftPosition([lat, lon]);
    setCenter([lat, lon]);
    setSearchText(result.display_name);
    setShowResults(false);
  }, []);

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lon = Number(pos.coords.longitude.toFixed(6));
        setDraftPosition([lat, lon]);
        setCenter([lat, lon]);
      },
      () => {}
    );
  }, []);

  const handleOpenInMaps = useCallback(() => {
    const pos = confirmedPosition || draftPosition;
    const q = address || (pos ? `${pos[0]},${pos[1]}` : "");
    const url = q
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
      : "https://maps.google.com";
    window.open(url, "_blank", "noopener");
  }, [address, confirmedPosition, draftPosition]);

  const displayPosition = locked ? confirmedPosition : draftPosition;
  const coordsLabel = displayPosition
    ? `${displayPosition[0]}, ${displayPosition[1]}`
    : null;

  return (
    <div className={`${styles.wrapper} ${locked ? styles.wrapperLocked : styles.wrapperEditing}`}>
      {!locked && (
        <div className={styles.searchRow}>
          <Input
            className={styles.searchInput}
            size="small"
            placeholder="Search location..."
            prefix={searching ? <Spin size="small" /> : <SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            allowClear
            onClear={() => { setSearchText(""); setSearchResults([]); setShowResults(false); }}
          />
          <Button size="small" icon={<AimOutlined />} onClick={handleLocate} title="Use current location" />
        </div>
      )}

      <div className={styles.mapContainer} style={{ position: "relative" }}>
        <MapContainer
          key={locked ? "locked" : "editing"}
          center={center}
          zoom={displayPosition ? 14 : DEFAULT_ZOOM}
          scrollWheelZoom={!locked}
          zoomControl={!locked}
          dragging={!locked}
          doubleClickZoom={!locked}
          touchZoom={!locked}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEvents onMapClick={handleMapClick} editing={!locked} />
          <MapCenterUpdater center={center} />
          {displayPosition && (
            <Marker
              draggable={!locked}
              position={displayPosition}
              ref={markerRef}
              icon={LockedIcon()}
              eventHandlers={{ dragend: handleMarkerDrag }}
            />
          )}
        </MapContainer>

        {locked && displayPosition && (
          <div className={styles.lockedBadge}>
            <LockOutlined /> Location pinned
          </div>
        )}

        {!locked && showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map((r, i) => (
              <div
                key={r.osm_id || i}
                className={styles.searchResultItem}
                onMouseDown={() => handleSelectResult(r)}
              >
                {r.display_name}
              </div>
            ))}
          </div>
        )}
        {!locked && showResults && searchText && !searching && searchResults.length === 0 && (
          <div className={styles.searchResults}>
            <div className={styles.searchNoResults}>No results found</div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <span className={styles.coords}>
          {coordsLabel ? `${coordsLabel}` : "No location pinned"}
        </span>
        <div className={styles.actions}>
          {locked ? (
            <>
              <Button size="small" icon={<GlobalOutlined />} onClick={handleOpenInMaps}>
                Open in Maps
              </Button>
              <Tooltip title={confirmedPosition ? "Edit pinned location" : "Pin a location"}>
                <Button size="small" icon={<UnlockOutlined />} onClick={enterEditMode}>
                  {confirmedPosition ? "Edit" : "Pin Location"}
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              <Button size="small" icon={<CloseOutlined />} onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirm}
                disabled={!draftPosition}
              >
                Confirm
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationPicker;
